import { useInfiniteQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';

import { useCioClient } from '~/shared/utils/useCioClient';
import { useMembership } from '~/techstyle-shared/react-accounts';

import {
  ConstructorGridFilterNames,
  ConstructorGridFilterTypes,
} from '../../BuilderConstructorGrid.builder';
import getCioBrowseResults from '../api/getCioBrowseResults';
import { BROWSE_API_RESULTS_STALE_TIME } from '../constants';

import useBuilderConstructorGridInputs from './useBuilderConstructorGridInputs';

// TODO: remove deprecated builder inputs
const getFilterNameAndFilterValue = ({
  filterType,
  collectionIdOverride,
  collectionSelection,
  collectionId, // @deprecated
  categoryIdOverride,
  categorySelection,
  categoryId, // @deprecated
  facet,
  facetOption,
}) => {
  switch (filterType) {
    case ConstructorGridFilterTypes.Collection:
      return {
        cioFilterName: ConstructorGridFilterNames.Collection,
        cioFilterValue:
          collectionIdOverride || collectionSelection?.value || collectionId,
      };
    case ConstructorGridFilterTypes.Category:
      return {
        cioFilterName: ConstructorGridFilterNames.Category,
        cioFilterValue:
          categoryIdOverride || categorySelection?.value || categoryId,
      };
    case ConstructorGridFilterTypes.Facet:
      return { cioFilterName: facet, cioFilterValue: facetOption };
    default:
      return { cioFilterName: '', cioFilterValue: '' };
  }
};

const useCioBrowseProducts = ({ selectedUrlFilters, allCioFacets }) => {
  const cioClient = useCioClient();
  const { isVip, isElite } = useMembership();
  const builderConstructorGridInputs = useBuilderConstructorGridInputs();

  const { asPath } = useRouter();
  const query = asPath.split('?')[1];
  const urlParams = new URLSearchParams(query);

  const { cioFilterName, cioFilterValue } = getFilterNameAndFilterValue(
    builderConstructorGridInputs
  );

  const selectedSortOption = urlParams.get('sort');

  const dynamicQueryParams = {
    cioFilterName,
    cioFilterValue,
    isElite,
    isVip,
    selectedUrlFilters,
    selectedSortOption,
  };

  const infiniteQueryResults = useInfiniteQuery({
    queryKey: ['CioBrowse results', dynamicQueryParams],
    queryFn: ({ pageParam = 1 }) => {
      return getCioBrowseResults({
        cioClient,
        page: pageParam,
        ...dynamicQueryParams,
      });
    },
    enabled: Boolean(cioClient) && Boolean(allCioFacets),
    staleTime: BROWSE_API_RESULTS_STALE_TIME,
    getNextPageParam: (lastPage, pages) => {
      return lastPage?.nextPage;
    },
    // We need keep previous data to be true so that we can show the disabled options state
    keepPreviousData: true,
  });

  return {
    cioFilterName,
    cioFilterValue,
    selectedSortOption,
    ...infiniteQueryResults,
  };
};

export default useCioBrowseProducts;
